import { useState, useMemo } from 'react'
import { useKeywordsGlobal, useBatchKeywordState, useUpdateKeywordBid, type Keyword, type KeywordFilters } from '@/api/keywords'
import { reconcileRequiredCount } from '@/api/client'
import { StateBadge, MatchTypeBadge } from '@/components/StatusBadge'
import DataTable, { type DataTableColumn } from '@/components/DataTable'
import Pagination from '@/components/Pagination'
import FilterBar from '@/components/FilterBar'
import DateRangePicker from '@/components/DateRangePicker'
import { useListQuery } from '@/lib/useListQuery'
import { Pause, Play, Archive, Check, X, AlertTriangle } from 'lucide-react'

function defaultDateRange(): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 30)
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) }
}

function fmtEur(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return `${n.toFixed(1)}%`
}
function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return n.toLocaleString('de-DE')
}

function BidCell({ keywordId, currentBid }: { keywordId: number; currentBid: string | null }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(currentBid ?? '')
  const updateBid = useUpdateKeywordBid()

  function handleSave() {
    if (!value || value === currentBid) { setEditing(false); return }
    updateBid.mutate({ id: keywordId, bid: value }, { onSuccess: () => setEditing(false) })
  }
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') handleSave()
    if (e.key === 'Escape') { setEditing(false); setValue(currentBid ?? '') }
  }
  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input type="text" value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={handleKeyDown} autoFocus
          className="h-7 w-20 rounded border border-[var(--ring)] bg-[var(--background)] px-2 text-right text-sm tabular-nums outline-none" />
        <button onClick={handleSave} className="text-emerald-600 hover:text-emerald-800" disabled={updateBid.isPending}>
          <Check className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => { setEditing(false); setValue(currentBid ?? '') }} className="text-zinc-400 hover:text-zinc-600">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    )
  }
  return (
    <button onClick={(e) => { e.stopPropagation(); setEditing(true) }} className="tabular-nums hover:underline">
      {currentBid ? `${parseFloat(currentBid).toFixed(2)}` : '—'}
    </button>
  )
}

export default function KeywordList() {
  const defaults = defaultDateRange()
  const { state, setPage, setPageSize, toggleSort, setSearch, setDateRange, setFilter } = useListQuery({
    pageSize: 50,
    sortBy: 'spend',
    sortDir: 'desc',
  })

  const [searchInput, setSearchInput] = useState(state.search ?? '')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  // M9 (Scheibe E): Zahl der Eintraege des letzten Batch-Laufs, deren Amazon-Zustand
  // ungeklaert blieb (HTTP 502 / RECONCILE_REQUIRED). 0 = kein Hinweis.
  const [reconcileCount, setReconcileCount] = useState(0)

  const filters: KeywordFilters = useMemo(() => ({
    limit: state.pageSize,
    offset: state.page * state.pageSize,
    sortBy: state.sortBy,
    sortDir: state.sortDir,
    search: state.search,
    matchType: state.filters['matchType'],
    state: state.filters['state'],
    campaignId: state.filters['campaignId'] ? Number(state.filters['campaignId']) : undefined,
    adGroupId: state.filters['adGroupId'] ? Number(state.filters['adGroupId']) : undefined,
    dateFrom: state.dateFrom ?? defaults.from,
    dateTo: state.dateTo ?? defaults.to,
  }), [state, defaults.from, defaults.to])

  const { data, isLoading, error } = useKeywordsGlobal(filters)
  const batchState = useBatchKeywordState()
  const kws = data?.keywords ?? []
  const total = data?.total ?? 0

  function onToggleAll() {
    if (selected.size === kws.length) setSelected(new Set())
    else setSelected(new Set(kws.map((k) => k.id)))
  }
  function onToggleOne(id: string | number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id as number)) next.delete(id as number)
      else next.add(id as number)
      return next
    })
  }
  async function handleBatchState(st: string) {
    if (selected.size === 0) return
    setReconcileCount(0)
    try {
      await batchState.mutateAsync({ ids: [...selected], state: st })
    } catch (err) {
      // Auswahl BEWUSST erhalten — nach einem Fehlschlag muesste der Operator sie
      // sonst neu zusammensuchen, gerade dann, wenn Teile des Batches offen sind.
      // `reconcileRequiredCount` liefert nur fuer den Batch-502 (`ackOrReconcile`)
      // einen Wert > 0; ein `nack(502, …)` ohne `result` und jeder andere Fehler
      // ergeben 0 und bleiben beim gewohnten globalen Toast.
      setReconcileCount(reconcileRequiredCount(err))
      return
    }
    setSelected(new Set())
  }

  const columns: DataTableColumn<Keyword>[] = [
    {
      key: 'text', label: 'Keyword', sortable: true, render: (k) => (
        <span className="flex items-center gap-1.5">
          <span className="font-medium truncate max-w-[240px]" title={k.text}>{k.text}</span>
          {k.isDuplicate && (
            <span title="Doppeltes Keyword (gleicher Text + Match-Type, enabled)">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
            </span>
          )}
        </span>
      )
    },
    { key: 'matchType', label: 'Match', render: (k) => <MatchTypeBadge matchType={k.matchType} /> },
    { key: 'campaignName', label: 'Kampagne', render: (k) => <span className="truncate max-w-[200px] block text-[var(--muted-foreground)]" title={k.campaignName ?? ''}>{k.campaignName ?? '—'}</span> },
    { key: 'adGroupName', label: 'Ad Group', render: (k) => <span className="truncate max-w-[160px] block text-[var(--muted-foreground)]" title={k.adGroupName ?? ''}>{k.adGroupName ?? '—'}</span> },
    { key: 'state', label: 'Status', render: (k) => <StateBadge state={k.state} /> },
    { key: 'bid', label: 'Bid', align: 'right', sortable: true, render: (k) => <BidCell keywordId={k.id} currentBid={k.bid} /> },
    { key: 'spend', label: 'Spend', align: 'right', sortable: true, render: (k) => fmtEur(k.spend) },
    { key: 'sales', label: 'Sales', align: 'right', sortable: true, render: (k) => fmtEur(k.sales) },
    { key: 'orders', label: 'Orders', align: 'right', sortable: true, render: (k) => fmtInt(k.orders) },
    { key: 'acos', label: 'ACoS', align: 'right', sortable: true, render: (k) => fmtPct(k.acos) },
    { key: 'clicks', label: 'Clicks', align: 'right', sortable: true, render: (k) => fmtInt(k.clicks) },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Keywords</h1>
        <span className="text-sm text-[var(--muted-foreground)]">{total.toLocaleString('de-DE')} gesamt</span>
      </div>

      {/* M9 (Scheibe E): So erreicht ein ungeklaerter Amazon-Zustand aus einem
          Batch-Write dieser Liste den Operator. Statt Toast — der verschwindet nach
          Sekunden, waehrend die betroffene Auswahl stehen bleibt. Bewusst KEIN Link
          auf den Prüfen-Tab: dessen Auswahl ist reiner Komponenten-State ohne URL. */}
      {reconcileCount > 0 && (
        <div role="alert" className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{`${reconcileCount} Einträge prüfbedürftig — Amazon-Zustand ungeklärt. Details: Inbox → Prüfen.`}</span>
        </div>
      )}

      <FilterBar
        search={searchInput}
        onSearch={(v) => { setSearchInput(v); setSearch(v) }}
        searchPlaceholder="Keyword suchen..."
        trailing={
          <DateRangePicker
            value={{ from: state.dateFrom ?? defaults.from, to: state.dateTo ?? defaults.to }}
            onChange={(r) => setDateRange(r.from, r.to)}
          />
        }
      >
        <select value={state.filters['matchType'] ?? ''} onChange={(e) => setFilter('matchType', e.target.value)}
          className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm">
          <option value="">Alle Match Types</option>
          <option value="broad">Broad</option>
          <option value="phrase">Phrase</option>
          <option value="exact">Exact</option>
        </select>
        <select value={state.filters['state'] ?? ''} onChange={(e) => setFilter('state', e.target.value)}
          className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm">
          <option value="">Alle Status</option>
          <option value="enabled">Enabled</option>
          <option value="paused">Paused</option>
          <option value="archived">Archived</option>
        </select>
      </FilterBar>

      {selected.size > 0 && (
        <div className="flex items-center gap-2 rounded-md bg-[var(--accent)] px-3 py-2 text-sm">
          <span className="font-medium">{selected.size} ausgewaehlt</span>
          <button onClick={() => handleBatchState('enabled')} className="ml-4 flex items-center gap-1 rounded px-2 py-1 text-emerald-700 hover:bg-emerald-100" disabled={batchState.isPending}>
            <Play className="h-3.5 w-3.5" /> Enable
          </button>
          <button onClick={() => handleBatchState('paused')} className="flex items-center gap-1 rounded px-2 py-1 text-amber-700 hover:bg-amber-100" disabled={batchState.isPending}>
            <Pause className="h-3.5 w-3.5" /> Pause
          </button>
          <button onClick={() => handleBatchState('archived')} className="flex items-center gap-1 rounded px-2 py-1 text-zinc-500 hover:bg-zinc-100" disabled={batchState.isPending}>
            <Archive className="h-3.5 w-3.5" /> Archivieren
          </button>
        </div>
      )}

      {error ? (
        <div className="py-12 text-center text-sm text-red-500">Fehler: {(error as Error).message}</div>
      ) : (
        <DataTable<Keyword>
          columns={columns}
          rows={kws}
          getRowId={(k) => k.id}
          sortBy={state.sortBy}
          sortDir={state.sortDir}
          onSort={toggleSort}
          selection={{ selected: selected as Set<string | number>, onToggle: onToggleOne, onToggleAll }}
          loading={isLoading}
          emptyMessage="Keine Keywords gefunden"
        />
      )}

      <Pagination
        page={state.page}
        pageSize={state.pageSize}
        totalItems={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  )
}
